import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "./MyNotesInboxItem";

class PrintInfo {
    public siteId: string;
    public siteName: string;
    public costCenterName: string;
    public siteStreetAddress: string;
    public siteCity: string;
    public siteState: string;
    public siteZipCode: string;
    public siteCountry: string;
    public sitePhoneNumber: string;
    public siteFaxNumber: string;

    public patientId: Guid;
    public patientName: string;
    public patientNAM: string;
    public patientExpiryDate: Date;
    public patientDOB: Date;
    public patientGender: string;
    public patientFileNumber: string;
    public patientStreetAddress: string;
    public patientCity: string;
    public patientState: string;
    public patientZipCode: string;
    public patientCountry: string;
    public patientPhoneNumber: string;
    public patientContactId: Guid;

    public static fromConsultationRequestInboxItem(item: ConsultationRequestsInboxItem, patient?: Patient): PrintInfo {
        if (!item)
            return null;

        const validationHelper = new ValidationHelper();

        let site = cacheStore.GetSiteById(item.SiteId);
        const siteAddress = site.Model.Adresses.firstOrDefault(x => x.IsPreferred) ||
            site.Model.Adresses.firstOrDefault(x => !x.IsPreferred);

        let printInfo = new PrintInfo();
        printInfo.siteId = item.SiteId;
        printInfo.siteName = site.Model.ClinicalName;
        printInfo.costCenterName = item.CostCenterId ? cacheStore.GetCostCenterNameById(item.CostCenterId) : "";
        printInfo.siteStreetAddress = siteAddress.StreetAddress;
        printInfo.siteCity = siteAddress.City;
        printInfo.siteState = cacheStore.GetLocalizedValueByLookupId(siteAddress.StateLookupId);
        printInfo.siteCountry = cacheStore.GetLocalizedValueByLookupId(siteAddress.CountryLookupId);
        printInfo.siteZipCode = validationHelper.getFormattedPostalCodeString(siteAddress.ZipCode, siteAddress.CountryLookupId, siteAddress.StateLookupId);

        this.setSitePhoneInfo(printInfo, site.Model.Phones);

        printInfo.patientId = item.PatientId;
        printInfo.patientName = item.PatientName;
        printInfo.patientNAM = item.PatientNAM;
        printInfo.patientExpiryDate = item.PatientNAMExpDate;
        printInfo.patientGender = item.PatientGender;
        printInfo.patientDOB = item.PatientDOB;
        printInfo.patientFileNumber = item.PatientFileNumber;
        printInfo.patientPhoneNumber = item.PatientPhone;

        this.setPatientInfo(printInfo, patient);

        return printInfo;
    }

    public static fromLabRequestInboxItem(item: RequestsInboxItem, patient?: Patient): PrintInfo {
        if (!item)
            return null;

        const validationHelper = new ValidationHelper();

        let site = cacheStore.GetSiteById(item.SiteId);
        const siteAddress = site.Model.Adresses.firstOrDefault(x => x.IsPreferred) ||
            site.Model.Adresses.firstOrDefault(x => !x.IsPreferred);

        let printInfo = new PrintInfo();
        printInfo.siteId = item.SiteId;
        printInfo.siteName = site.Model.ClinicalName;
        printInfo.costCenterName = item.CostCenterId ? cacheStore.GetCostCenterNameById(item.CostCenterId) : "";
        printInfo.siteStreetAddress = siteAddress.StreetAddress;
        printInfo.siteCity = siteAddress.City;
        printInfo.siteState = cacheStore.GetLocalizedValueByLookupId(siteAddress.StateLookupId);
        printInfo.siteCountry = cacheStore.GetLocalizedValueByLookupId(siteAddress.CountryLookupId);
        printInfo.siteZipCode = validationHelper.getFormattedPostalCodeString(siteAddress.ZipCode, siteAddress.CountryLookupId, siteAddress.StateLookupId);

        this.setSitePhoneInfo(printInfo, site.Model.Phones);

        printInfo.patientId = item.PatientId;
        printInfo.patientName = item.PatientName;

        this.setPatientInfo(printInfo, patient);

        return printInfo;
    }

    private static setSitePhoneInfo(printInfo: PrintInfo, phones: PhoneNumber[]): void {
        if (!printInfo || !phones)
            return;

        const validationHelper = new ValidationHelper();
        for (let phone of phones) {
            const formattedPhoneNumber = validationHelper.getFormattedPhoneNumberString(phone.Number.toString(), phone.CountryLookupId, phone.StateLookupId);
            const phoneType = cacheStore.GetLookupById(phone.PhoneTypeLookupId);

            if (phoneType.en === "Fax") {
                printInfo.siteFaxNumber = formattedPhoneNumber;
            }
            else {
                printInfo.sitePhoneNumber = formattedPhoneNumber;
            }
        }
    }

    private static setPatientInfo(printInfo: PrintInfo, patient: Patient): void {
        if (!printInfo || !patient)
            return;

        const validationHelper = new ValidationHelper();
        let healthCard = !isArrayNullOrUndefinedOrEmpty(patient.HealthCards) ? patient.HealthCards.firstOrDefault() : null;

        printInfo.patientName = patient.GetFormattedNameWithPrefix();
        printInfo.patientNAM = healthCard ? healthCard.Number : "";
        printInfo.patientExpiryDate = healthCard ? healthCard.ExpiryDate : null;
        printInfo.patientDOB = patient.IndividualResponse.DateOfBirth;
        printInfo.patientGender = patient.GetFormattedGender();
        printInfo.patientFileNumber = patient.GetFormattedIdentifiers();
        printInfo.patientContactId = patient.IndividualResponse.Contact_id;

        let patientAddress = patient.Addresses.firstOrDefault(x => x.IsPreferred) || patient.Addresses.firstOrDefault(x => !x.IsPreferred);
        if (patientAddress) {
            printInfo.patientStreetAddress = patientAddress.StreetAddress;
            printInfo.patientState = cacheStore.GetLocalizedValueByLookupId(patientAddress.StateLookupId);
            printInfo.patientCity = patientAddress.City;
            printInfo.patientCountry = cacheStore.GetLocalizedValueByLookupId(patientAddress.CountryLookupId);
            printInfo.patientZipCode = validationHelper.getFormattedPostalCodeString(patientAddress.ZipCode, patientAddress.CountryLookupId, patientAddress.StateLookupId);
        }
    }
}

export { PrintInfo };
